import React, { useState, useEffect, Suspense } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import styled from "styled-components";
import dynamic from "next/dynamic";
import { useRouter } from "next/navigation";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Update imports to point to src/pages/ where applicable
const Header = dynamic(() => import("./header"), { ssr: false });
const HowItWorks = dynamic(() => import("./how-it-works"), { ssr: false });
const WhyChooseUs = dynamic(() => import("./WhyChooseUs"), { ssr: false });
const DiscoverOurServices = dynamic(() => import("./DiscoverOurServices"), { ssr: false });
const Testimonies = dynamic(() => import("./testimonies"), { ssr: false });
const FAQs = dynamic(() => import("./faqs"), { ssr: false });
const Samples = dynamic(() => import("./samples"), { ssr: false });
const FreeServices = dynamic(() => import("./free-services"), { ssr: false });
const Footer = dynamic(() => import("./Footer"), { ssr: false });
const Offers = dynamic(() => import("./offers"), { ssr: false });
const AboutUs = dynamic(() => import("./about-us"), { ssr: false });
const FairUsePolicy = dynamic(() => import("./fair-use-policy"), { ssr: false });
const CitationsGenerator = dynamic(() => import("./citations-generator"), { ssr: false });
const PaymentPolicy = dynamic(() => import("./payment-policy"), { ssr: false });
const TermsConditions = dynamic(() => import("./terms-conditions"), { ssr: false });
const Blog = dynamic(() => import("./blog"), { ssr: false });
const ContactUs = dynamic(() => import("./contact-us"), { ssr: false });

const PageWrapper = styled(motion.div)`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  position: relative;
  background-color: white;
  color: #1f2937;
`;

const ContentWrapper = styled.div`
  flex: 1;
  padding: 100px 2rem 2rem;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    padding: 80px 1rem 1rem;
  }

  @media (max-width: 640px) {
    padding: 60px 0.5rem 1rem;
  }
`;

const FooterWrapper = styled.div`
  width: 100%;
  position: relative;
  z-index: 1;
`;

const ErrorFallback = () => (
  <div style={{ padding: "2rem", textAlign: "center" }}>
    <h1>Something went wrong</h1>
    <p>Please try again later.</p>
  </div>
);

export default function DynamicPage({ page }) {
  const router = useRouter();
  const [activePage, setActivePage] = useState(page || "home");
  const { scrollY } = useScroll();
  const headerOpacity = useTransform(scrollY, [0, 200], [1, 0.95]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activePage]);

  const headerVariants = {
    hidden: { y: -100, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const handleNavigate = (newPage) => {
    setActivePage(newPage);
    router.push(`/${newPage === "home" ? "" : newPage}`);
  };

  const renderPageContent = () => {
    try {
      switch (activePage) {
        case "how-it-works":
          return <HowItWorks />;
        case "DiscoverOurServices":
          return <DiscoverOurServices />;
        case "offers":
          return <Offers />;
        case "testimonies":
          return <Testimonies />;
        case "about-us":
          return <AboutUs />;
        case "fair-use-policy":
          return <FairUsePolicy />;
        case "citations-generator":
          return <CitationsGenerator />;
        case "samples":
          return <Samples />;
        case "faqs":
          return <FAQs />;
        case "free-services":
          return <FreeServices />;
        case "payment-policy":
          return <PaymentPolicy />;
        case "terms-conditions":
          return <TermsConditions />;
        case "blog":
          return <Blog />;
        case "contact-us":
          return <ContactUs />;
        default:
          return null;
      }
    } catch (error) {
      console.error("Error rendering page content:", error);
      return <ErrorFallback />;
    }
  };

  if (activePage === "home") {
    return (
      <PageWrapper
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <motion.div style={{ opacity: headerOpacity }}>
          <Header onNavigate={handleNavigate} activePage={activePage} />
        </motion.div>
        <ContentWrapper>
          <Suspense fallback={<div>Loading...</div>}>
            {/* Simplified homepage content since LandingPage, our-numbers, and our-experts are missing */}
            <div>
              <h1>Welcome to EssayLite</h1>
              <p>Get expert essay help with high-quality academic writing services.</p>
            </div>
            <HowItWorks />
            <WhyChooseUs />
            <DiscoverOurServices />
            <Testimonies />
            <FreeServices />
          </Suspense>
        </ContentWrapper>
        <FooterWrapper>
          <Footer onNavigate={handleNavigate} activePage={activePage} />
        </FooterWrapper>
        <ToastContainer position="top-right" autoClose={3000} />
      </PageWrapper>
    );
  }

  return (
    <PageWrapper
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div style={{ opacity: headerOpacity }}>
        <Header onNavigate={handleNavigate} activePage={activePage} />
      </motion.div>
      <ContentWrapper>
        <Suspense fallback={<div>Loading...</div>}>
          {renderPageContent()}
        </Suspense>
      </ContentWrapper>
      <FooterWrapper>
        <Footer onNavigate={handleNavigate} activePage={activePage} />
      </FooterWrapper>
      <ToastContainer position="top-right" autoClose={3000} />
    </PageWrapper>
  );
}

export async function getServerSideProps({ params }) {
  const page = params?.page || "home";
  return {
    props: { page },
  };
}

export async function generateMetadata({ params }) {
  const page = params?.page || "home";

  const titles = {
    home: "Expert Essay Help & Academic Writing Assistance",
    "about-us": "About Our Academic Support Team & Mission",
    "how-it-works": "How It Works – Steps to Academic Success",
    "DiscoverOurServices": "Academic Writing Services & Solutions",
    "offers": "Special Offers – Affordable Academic Help",
    "testimonies": "Testimonials – Student Success Stories",
    "citations-generator": "Free Citation Generator – Academic Tools",
    "samples": "Essay Samples – High-Quality Writing Examples",
    "free-services": "Free Academic Tools & Resources",
    "blog": "Blog – Academic Writing Tips & Insights",
    "contact-us": "Contact Us – Get Personalized Support",
    "fair-use-policy": "Fair Use Policy – Academic Writing Guidelines",
    "payment-policy": "Payment Policy – Secure Transactions",
    "terms-conditions": "Terms & Conditions – Service Agreement",
    "faqs": "Frequently Asked Questions – Academic Support",
    signup: "Sign Up for Expert Academic Writing Support",
    login: "Log In to Access Academic Writing Services",
  };

  const descriptions = {
    home: "Get expert essay help with high-quality academic writing services. Craft essays, research papers, and assignments with professional support, fast delivery, and plagiarism-free content.",
    "about-us": "Discover our mission to empower students with expert academic support and personalized guidance. Meet our dedicated writing team!",
    "how-it-works": "Learn how our process delivers top-notch academic writing support, from order placement to final paper delivery.",
    "DiscoverOurServices": "Explore comprehensive services like essays, research papers, dissertations, and editing tailored to your needs.",
    "offers": "Take advantage of exclusive offers and discounts for affordable, high-quality academic writing support.",
    "testimonies": "Hear from students who succeeded with our writing services. Read their inspiring testimonials!",
    "citations-generator": "Create accurate APA, MLA citations with our free citation generator for students and researchers.",
    "samples": "Browse high-quality essay samples to inspire your academic writing projects.",
    "free-services": "Access free tools like citation generators and plagiarism checkers to enhance your studies.",
    "blog": "Explore tips and guides to boost your essay writing and academic performance.",
    "contact-us": "Get in touch with us for personalized academic support and inquiries.",
    "fair-use-policy": "Understand our fair use policy for academic writing services.",
    "payment-policy": "Learn about our secure payment policies for your peace of mind.",
    "terms-conditions": "Review the terms and conditions governing our academic writing services.",
    "faqs": "Find answers to common questions about our academic support services.",
    signup: "Join EssayLite to get expert help with essays, research papers, and assignments. Sign up today!",
    login: "Log in to EssayLite to access personalized academic writing support and manage your orders.",
  };

  const keywords = {
    home: [
      "essay help",
      "academic writing",
      "assignment help",
      "research paper writing",
      "write my essay",
      "plagiarism-free essays",
      "cheap essay writing",
      "urgent assignment help",
      "professional essay writers",
      "online writing services",
    ],
    "about-us": [
      "essay help",
      "academic writing",
      "academic writing experts",
      "professional writing team",
      "student academic support",
      "essay writing professionals",
    ],
    "how-it-works": [
      "essay help",
      "assignment help",
      "how to write an essay",
      "academic writing process",
      "order essay online",
      "steps to write a paper",
    ],
    "DiscoverOurServices": [
      "academic writing",
      "research paper writing",
      "essay editing services",
      "research paper writing service",
      "dissertation writing help",
    ],
    "offers": [
      "essay help",
      "cheap essay writing",
      "cheap assignment writing",
      "essay writing discounts",
      "affordable academic help",
    ],
    "testimonies": [
      "essay help",
      "academic writing",
      "essay writing reviews",
      "student writing testimonials",
      "academic writing feedback",
    ],
    "citations-generator": [
      "citation generator",
      "APA citation help",
      "MLA citation maker",
      "free citation tool",
      "academic referencing",
    ],
    "samples": [
      "essay help",
      "research paper writing",
      "essay samples",
      "research paper examples",
      "sample academic papers",
    ],
    "free-services": [
      "essay help",
      "free academic tools",
      "free essay checker",
      "free writing resources",
      "free plagiarism checker",
    ],
    "blog": [
      "essay help",
      "academic writing",
      "academic writing blog",
      "essay writing tips",
      "student writing guides",
    ],
    "contact-us": [
      "essay help",
      "contact academic support",
      "student support services",
      "academic writing inquiries",
      "get help with essays",
    ],
    "fair-use-policy": [
      "essay help",
      "academic writing policy",
      "fair use guidelines",
      "writing service rules",
    ],
    "payment-policy": [
      "essay help",
      "payment policy",
      "secure payment",
      "academic writing payment",
    ],
    "terms-conditions": [
      "essay help",
      "terms and conditions",
      "writing service agreement",
      "academic support terms",
    ],
    "faqs": [
      "essay help",
      "academic writing faqs",
      "student support questions",
      "writing service help",
    ],
    signup: [
      "sign up for essay help",
      "join academic writing service",
      "register for assignment help",
      "academic support signup",
      "essay writing account",
    ],
    login: [
      "log in to essay help",
      "academic writing login",
      "access assignment help",
      "student writing login",
      "essay service login",
    ],
  };

  const ogImages = {
    home: { url: "/images/home-og.jpg", width: 1200, height: 630, alt: "Academic Writing Support" },
    "about-us": { url: "/images/about-us-og.jpg", width: 1200, height: 630, alt: "Academic Support Team" },
    "how-it-works": { url: "/images/how-it-works-og.jpg", width: 1200, height: 630, alt: "Writing Process" },
    "DiscoverOurServices": { url: "/images/services-og.jpg", width: 1200, height: 630, alt: "Writing Services" },
    "offers": { url: "/images/offers-og.jpg", width: 1200, height: 630, alt: "Special Offers" },
    "testimonies": { url: "/images/testimonials-og.jpg", width: 1200, height: 630, alt: "Student Testimonials" },
    "citations-generator": { url: "/images/citations-og.jpg", width: 1200, height: 630, alt: "Citation Generator" },
    "samples": { url: "/images/samples-og.jpg", width: 1200, height: 630, alt: "Essay Samples" },
    "free-services": { url: "/images/free-services-og.jpg", width: 1200, height: 630, alt: "Free Tools" },
    "blog": { url: "/images/blog-og.jpg", width: 1200, height: 630, alt: "Academic Blog" },
    "contact-us": { url: "/images/contact-us-og.jpg", width: 1200, height: 630, alt: "Contact Support" },
    "fair-use-policy": { url: "/images/fair-use-og.jpg", width: 1200, height: 630, alt: "Fair Use Policy" },
    "payment-policy": { url: "/images/payment-og.jpg", width: 1200, height: 630, alt: "Payment Policy" },
    "terms-conditions": { url: "/images/terms-og.jpg", width: 1200, height: 630, alt: "Terms & Conditions" },
    "faqs": { url: "/images/faqs-og.jpg", width: 1200, height: 630, alt: "FAQs" },
    signup: { url: "/images/signup-og.jpg", width: 1200, height: 630, alt: "Sign Up for Academic Support" },
    login: { url: "/images/login-og.jpg", width: 1200, height: 630, alt: "Log In to Academic Services" },
  };

  return {
    title: titles[page] || titles.home,
    description: descriptions[page] || descriptions.home,
    keywords: keywords[page] || keywords.home,
    robots: {
      index: true,
      follow: true,
      googleBot: {
        index: true,
        follow: true,
        "max-video-preview": -1,
        "max-image-preview": "large",
        "max-snippet": -1,
      },
    },
    openGraph: {
      type: "website",
      locale: "en_US",
      url: `https://www.essaylite.com/${page === "home" ? "" : page}`,
      title: titles[page] || titles.home,
      description: descriptions[page] || descriptions.home,
      siteName: "EssayLite",
      images: [ogImages[page] || ogImages.home],
    },
    twitter: {
      card: "summary_large_image",
      site: "@essaylite",
      creator: "@essaylite",
      title: titles[page] || titles.home,
      description: descriptions[page] || descriptions.home,
      images: [(ogImages[page] || ogImages.home).url],
    },
    alternates: {
      canonical: `https://www.essaylite.com/${page === "home" ? "" : page}`,
      languages: {
        "en-US": `https://www.essaylite.com/${page === "home" ? "" : page}`,
        "en-CA": `https://www.essaylite.com/${page === "home" ? "" : page}`,
        "en-AU": `https://www.essaylite.com/${page === "home" ? "" : page}`,
        "en-GB": `https://www.essaylite.com/${page === "home" ? "" : page}`,
        "en-IE": `https://www.essaylite.com/${page === "home" ? "" : page}`,
        "en-NZ": `https://www.essaylite.com/${page === "home" ? "" : page}`,
        "en-SE": `https://www.essaylite.com/${page === "home" ? "" : page}`,
        "en-NO": `https://www.essaylite.com/${page === "home" ? "" : page}`,
        "en-DK": `https://www.essaylite.com/${page === "home" ? "" : page}`,
        "en-FI": `https://www.essaylite.com/${page === "home" ? "" : page}`,
        "en-NL": `https://www.essaylite.com/${page === "home" ? "" : page}`,
        "en-BE": `https://www.essaylite.com/${page === "home" ? "" : page}`,
        "en-AT": `https://www.essaylite.com/${page === "home" ? "" : page}`,
        "en-CH": `https://www.essaylite.com/${page === "home" ? "" : page}`,
        "en-DE": `https://www.essaylite.com/${page === "home" ? "" : page}`,
        "en-ES": `https://www.essaylite.com/${page === "home" ? "" : page}`,
        "en-FR": `https://www.essaylite.com/${page === "home" ? "" : page}`,
        "en-IT": `https://www.essaylite.com/${page === "home" ? "" : page}`,
      },
    },
    viewport: {
      width: "device-width",
      initialScale: 1,
      maximumScale: 1,
    },
    icons: {
      icon: "/favicon.ico",
      shortcut: "/favicon.ico",
      apple: "/favicon.ico",
    },
    other: {
      "application/ld+json": JSON.stringify([
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://www.essaylite.com/",
            },
            ...(page !== "home"
              ? [
                  {
                    "@type": "ListItem",
                    position: 2,
                    name: page
                      .split("-")
                      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(" "),
                    item: `https://www.essaylite.com/${page}`,
                  },
                ]
              : []),
          ],
        },
        {
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "EssayLite",
          url: "https://www.essaylite.com/",
          logo: "https://www.essaylite.com/favicon.ico",
          sameAs: [
            "https://twitter.com/essaylite",
            "https://www.linkedin.com/company/essaylite",
          ],
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "Customer Service",
            email: "support@essaylite.com",
          },
          description:
            "A premier academic support platform dedicated to empowering students with expert writing guidance and innovative tools.",
        },
        ...(page === "home"
          ? [
              {
                "@context": "https://schema.org",
                "@type": "ItemList",
                itemListElement: [
                  {
                    "@type": "ListItem",
                    position: 1,
                    name: "Join EssayLite",
                    item: "https://www.essaylite.com/signup",
                    description: "Begin your journey with us for expert academic support.",
                  },
                  {
                    "@type": "ListItem",
                    position: 2,
                    name: "About Us",
                    item: "https://www.essaylite.com/about-us",
                    description: "Learn about our mission and expert writing team.",
                  },
                  {
                    "@type": "ListItem",
                    position: 3,
                    name: "Contact Us",
                    item: "https://www.essaylite.com/contact-us",
                    description: "Get in touch for personalized support.",
                  },
                  {
                    "@type": "ListItem",
                    position: 4,
                    name: "Samples",
                    item: "https://www.essaylite.com/samples",
                    description: "Explore high-quality essay samples.",
                  },
                ],
              },
              {
                "@context": "https://schema.org",
                "@type": "SiteNavigationElement",
                name: "Main Navigation",
                url: "https://www.essaylite.com/",
                description: "Explore our academic writing services and resources.",
                potentialAction: [
                  {
                    "@type": "ReadAction",
                    target: "https://www.essaylite.com/",
                    name: "Home",
                  },
                  {
                    "@type": "ReadAction",
                    target: "https://www.essaylite.com/about-us",
                    name: "About Us",
                  },
                  {
                    "@type": "ReadAction",
                    target: "https://www.essaylite.com/contact-us",
                    name: "Contact Us",
                  },
                  {
                    "@type": "ReadAction",
                    target: "https://www.essaylite.com/samples",
                    name: "Samples",
                  },
                  {
                    "@type": "ReadAction",
                    target: "https://www.essaylite.com/signup",
                    name: "Join EssayLite",
                  },
                ],
              },
            ]
          : page === "about-us"
          ? [
              {
                "@context": "https://schema.org",
                "@type": "AboutPage",
                name: "About EssayLite",
                description:
                  "Discover our mission to empower students with expert academic support and personalized guidance.",
                mainEntityOfPage: "https://www.essaylite.com/about-us",
              },
            ]
          : page === "samples"
          ? [
              {
                "@context": "https://schema.org",
                "@type": "ItemList",
                itemListElement: [
                  {
                    "@type": "ListItem",
                    position: 1,
                    name: "Sample: Othello Analysis",
                    item: "https://www.essaylite.com/samples/othello",
                    description:
                      "The Tragedy of Othello: A Thematic Analysis of Jealousy.",
                  },
                ],
              },
            ]
          : page === "contact-us"
          ? [
              {
                "@context": "https://schema.org",
                "@type": "ContactPage",
                name: "Contact EssayLite",
                description:
                  "Get in touch with us for personalized academic support and inquiries.",
                mainEntityOfPage: "https://www.essaylite.com/contact-us",
              },
            ]
          : []),
      ]),
    },
  };
}